import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox } from '@repo/common';
import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/16px/icon_16px_check.svg';
import { getOpsStaff } from '../../../api/twoDays/ops.api';
import TdButton from '../Button';
import { cn } from '../model/derive';
import { KST, formatDue, fromInputValue, toInputValue } from '../model/time';
import { useNow } from '../model/useWorkboard';
import { Field, Modal, ModalButton, Select, TextArea, TextInput } from '../Modal';
import { ArrowIcon, StatusChip } from '../primitives';
import TaskListEditor from '../TaskListEditor';
import TaskListGroups from '../TaskListGroups';
import { baseItemsOf, handoffTypeByJobs } from '../model/taskList';
import { SKILL_TAGS } from '../model/skillTags';
import TeamFiles, { FilesAlert, useTeamFiles } from './TeamFiles';
import {
  HANDOFFS,
  JOBS,
  END_H,
  addHours,
  LAST_DUE_H,
  NPC_LEAD,
  formatKst,
  jobLabel,
  receivingRoleLabel,
  leaderOf,
  periodOf,
  lockedNotice,
  formatMinutes,
  nextTaskCode,
  personForRole,
  receiverPreview,
  receiverRoleFor,
  setupMode,
} from './opsModel';

/* ── 공통 ───────────────────────────────────────────────── */

const toInput = (iso) => (iso ? toInputValue(new Date(iso), KST) : '');
const fromInput = (v) => fromInputValue(v, KST)?.toISOString() ?? null;
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * 단계의 편집본. 저장된 값이 바뀌면(이 단계를 저장했거나, 앞 단계 저장으로 새로 생겼거나)
 * 고치던 내용이 없을 때만 편집본을 새 값으로 맞춘다.
 */
const useDraft = (saved) => {
  const [draft, setDraft] = useState(saved);
  const [base, setBase] = useState(saved);
  if (!same(saved, base)) {
    setBase(saved);
    if (same(draft, base)) setDraft(saved);
  }
  return [draft, setDraft];
};

/** 저장 중에는 버튼을 막는다. call 이 true 를 돌려주면(저장 성공) then 을 부른다 */
const useSaving = () => {
  const [saving, setSaving] = useState(false);
  const runSave = async (call, then) => {
    setSaving(true);
    const ok = await call();
    setSaving(false);
    if (ok) then?.();
  };
  return [saving, runSave];
};

/** 저장 안 한 변경이 있는지 단계 목록에 알린다 ("Not saved") */
const useReportDirty = (dispatch, step, dirty) => {
  useEffect(() => {
    dispatch({ type: 'dirty', step, value: dirty });
  }, [dispatch, step, dirty]);
  useEffect(() => () => dispatch({ type: 'dirty', step, value: false }), [dispatch, step]);
};

/** 단계 카드 — 제목·설명 / 본문 / 아래쪽 안내 + 저장 버튼 */
/** 시작 뒤 경고 첫 문장 — 진행 중 · 끝난 뒤 */
const liveLead = (mode) =>
  mode === 'ended'
    ? 'The project has ended. Workboards stay read-only for members, but your changes still show there and in reports.'
    : 'The project is active.';

/** notice 는 회색 안내, warning 은 노란 경고 (시작 뒤에 고치면 워크보드에 바로 반영된다는 안내 등) */
export const StepCard = ({
  title,
  desc,
  notice,
  warning,
  children,
  hint,
  action,
  disabled,
  onAction,
}) => (
  <section className='flex min-w-0 flex-1 flex-col rounded-2xl border border-green-deep03/[0.08] bg-grayscale-gray00 shadow-4'>
    <header className='border-b border-green-deep03/[0.08] px-5 py-6 sm:px-7'>
      <h2 className='text-[20px] font-medium leading-[130%] text-text-primary'>{title}</h2>
      <p className='mt-1 text-[14px] leading-[150%] text-text-secondary'>{desc}</p>
      {/* 진행 중·종료 후 — 무엇을 바꿀 수 있는지 */}
      {notice && (
        <p className='mt-4 flex items-center gap-2 rounded-lg bg-green-deep03/[0.06] px-3 py-2.5 text-[13px] text-text-primary'>
          <span aria-hidden className='h-1.5 w-1.5 shrink-0 rounded-full bg-grayscale-gray05' />
          {notice}
        </p>
      )}
      {warning && (
        <p
          role='note'
          className='mt-4 flex items-start gap-2 rounded-lg bg-functional-warning01 px-3 py-2.5 text-[13px] leading-[150%] text-functional-warning04'
        >
          <span
            aria-hidden
            className='mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-functional-warning03'
          />
          {warning}
        </p>
      )}
    </header>
    <div className='flex flex-col gap-6 px-5 py-6 sm:px-7'>{children}</div>
    {action && (
      <footer className='flex items-center justify-end gap-4 border-t border-green-deep03/[0.08] px-5 py-4 sm:px-7'>
        {hint && <span className='text-[13px] text-text-secondary'>{hint}</span>}
        <ModalButton primary disabled={disabled} onClick={onAction}>
          {action}
        </ModalButton>
      </footer>
    )}
  </section>
);

/** 계산된 값(읽기 전용) 칸 */
const ReadOnly = ({ value, placeholder, right }) => (
  <div className='flex h-10 items-center justify-between gap-3 rounded-lg bg-green-deep03/[0.04] px-3 text-[14px]'>
    <span className={value ? 'text-text-primary' : 'text-text-placeholder'}>
      {value || placeholder}
    </span>
    {right}
  </div>
);

const Hint = ({ children }) => (
  <p className='text-[12px] leading-[150%] text-text-secondary'>{children}</p>
);

export const EmptyBox = ({ title, desc, children }) => (
  <div className='flex flex-col items-center gap-2 rounded-xl border border-dashed border-green-deep03/20 px-6 py-12 text-center'>
    <p className='text-[15px] font-medium text-text-primary'>{title}</p>
    <p className='text-[14px] text-text-secondary'>{desc}</p>
    {children}
  </div>
);

/* ── 1. Project ─────────────────────────────────────────── */

export const ProjectStep = ({ project, dispatch, api, onSaved }) => {
  const initial = {
    name: project?.name ?? '',
    domain: project?.domain ?? '',
    leaderStartAt: project?.leaderStartAt ?? null,
    endAt: project?.endAt ?? null,
  };
  const [draft, setDraft] = useState(initial);
  const [editEnd, setEditEnd] = useState(false);
  // 리더 시작을 바꾸면 종료는 기본값(+49h)으로 다시 계산한다 — 직접 고친 경우만 유지
  const [endTouched, setEndTouched] = useState(
    !!project && project.endAt !== periodOf(project.leaderStartAt).endAt,
  );
  const period = periodOf(draft.leaderStartAt, endTouched ? draft.endAt : null);
  const dirty =
    !same(draft, initial) && (!!project || !!(draft.name || draft.domain || draft.leaderStartAt));
  useReportDirty(dispatch, 'project', dirty);

  const filled = draft.name.trim() && draft.domain.trim() && draft.leaderStartAt;
  const [saving, runSave] = useSaving();
  const mode = setupMode(project);
  // 시작한 뒤에도 이름·도메인·종료 시각은 고친다(경고). 리더 시작은 워크보드 마감의 기준이라 막는다. 끝나면 읽기 전용
  // 시작 뒤 · 끝난 뒤에도 이름·도메인·종료 시각은 고친다(경고). 리더 시작만 막는다
  const fieldsLocked = false;
  const startLocked = mode !== 'draft';
  const canEditEnd = true;
  const save = () => {
    const fields = {
      name: draft.name.trim(),
      domain: draft.domain.trim(),
      leaderStartAt: draft.leaderStartAt,
      endAt: period.endAt,
    };
    runSave(() => (project ? api.saveProject(fields) : api.create(fields)), onSaved);
  };

  return (
    <StepCard
      title='Project'
      desc='Name, domain and schedule. A new project starts as draft.'
      warning={
        mode !== 'draft'
          ? `${liveLead(mode)} Changes show on every workboard right away. Leader start can’t change after the project starts.`
          : null
      }
      hint={
        !filled ? 'Fill in all fields to create' : project && !dirty ? 'No changes to save' : null
      }
      action={project ? 'Save' : 'Create project'}
      disabled={saving || !filled || (project && !dirty)}
      onAction={save}
    >
      <div className='grid gap-6 border-b border-green-deep03/[0.08] pb-6 sm:grid-cols-2 lg:max-w-[640px]'>
        <Field label='Project name' htmlFor='ops-name'>
          {fieldsLocked ? (
            <ReadOnly value={draft.name} />
          ) : (
            <TextInput
              id='ops-name'
              placeholder='e.g. Delivery app: the last 100 m'
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            />
          )}
        </Field>
        <Field label='Domain' htmlFor='ops-domain'>
          {fieldsLocked ? (
            <ReadOnly value={draft.domain} />
          ) : (
            <TextInput
              id='ops-domain'
              placeholder='e.g. Consumer app'
              value={draft.domain}
              onChange={(e) => setDraft((d) => ({ ...d, domain: e.target.value }))}
            />
          )}
        </Field>
      </div>

      <div className='flex flex-col gap-4 lg:max-w-[640px]'>
        <h3 className='text-[15px] font-medium text-text-primary'>Period (KST)</h3>
        <div className='grid gap-x-6 gap-y-5 sm:grid-cols-2'>
          <Field label='Leader start' htmlFor='ops-leader-start'>
            {startLocked ? (
              <ReadOnly value={formatKst(draft.leaderStartAt)} />
            ) : (
              <TextInput
                id='ops-leader-start'
                type='datetime-local'
                value={toInput(draft.leaderStartAt)}
                onChange={(e) => {
                  const iso = fromInput(e.target.value);
                  setDraft((d) => ({
                    ...d,
                    leaderStartAt: iso,
                    endAt: endTouched ? d.endAt : null,
                  }));
                }}
              />
            )}
          </Field>
          <div className='flex flex-col gap-2'>
            <span className='text-[13px] font-medium text-text-primary'>Team start</span>
            <ReadOnly value={formatKst(period.teamStartAt)} placeholder='Set leader start first' />
            <Hint>Leader start + 1h. Members can open the workboard from here.</Hint>
          </div>
          <div className='flex flex-col gap-2'>
            <span className='text-[13px] font-medium text-text-primary'>Last task due</span>
            <ReadOnly value={formatKst(period.lastDueAt)} placeholder='Set leader start first' />
            <Hint>Leader start + {LAST_DUE_H}h. No task can be due later.</Hint>
          </div>
          <div className='flex flex-col gap-2'>
            <span className='text-[13px] font-medium text-text-primary'>Project end</span>
            {editEnd ? (
              <TextInput
                type='datetime-local'
                aria-label='Project end'
                autoFocus
                value={toInput(period.endAt)}
                min={toInput(period.lastDueAt)}
                onChange={(e) => {
                  setEndTouched(true);
                  setDraft((d) => ({ ...d, endAt: fromInput(e.target.value) }));
                }}
                onBlur={() => setEditEnd(false)}
              />
            ) : (
              <ReadOnly
                value={formatKst(period.endAt)}
                placeholder='Set leader start first'
                right={
                  draft.leaderStartAt &&
                  canEditEnd && (
                    <button
                      type='button'
                      onClick={() => setEditEnd(true)}
                      className='text-[13px] font-medium text-text-secondary hover:text-text-primary'
                    >
                      Edit
                    </button>
                  )
                }
              />
            )}
            <Hint>
              {draft.leaderStartAt
                ? `Leader start + ${END_H}h, incl. a 2h buffer. Only ops can change it.`
                : `Fills in as leader start + ${END_H}h.`}
            </Hint>
          </div>
        </div>
      </div>
    </StepCard>
  );
};

/* ── 2. Brief ───────────────────────────────────────────── */

export const BriefStep = ({ project, dispatch, api, onSaved }) => {
  const initial = project.brief;
  const [draft, setDraft] = useDraft(initial);
  const dirty = !same(draft, initial);
  useReportDirty(dispatch, 'brief', dirty);
  const filled = draft.scenario.trim() && draft.deliverable.trim();
  const mode = setupMode(project);
  // 시작 뒤 · 끝난 뒤에도 고친다(경고)
  const locked = false;
  const toggle = (key, value) =>
    !locked &&
    setDraft((d) => ({
      ...d,
      [key]: d[key].includes(value) ? d[key].filter((x) => x !== value) : [...d[key], value],
    }));

  const [saving, runSave] = useSaving();
  // 브리프를 처음 저장하면 서버가 requested 체크포인트 7개를 만든다
  const save = () =>
    runSave(
      () =>
        api.saveBrief({
          ...draft,
          scenario: draft.scenario.trim(),
          deliverable: draft.deliverable.trim(),
        }),
      onSaved,
    );

  return (
    <StepCard
      title='Brief'
      desc='What teams work on and hand off in 2 days. Saving the brief creates the requested checkpoints.'
      warning={
        mode !== 'draft'
          ? `${liveLead(mode)} Members see the new scenario and deliverable right away. Changed handoff types only apply to tasks added from now on.`
          : null
      }
      hint={
        !filled
          ? 'Add the situation and what to build'
          : project.done.brief && !dirty
            ? 'No changes to save'
            : null
      }
      action={locked ? null : 'Save'}
      disabled={saving || !filled || (project.done.brief && !dirty)}
      onAction={save}
    >
      <div className='flex flex-col gap-6 lg:flex-row lg:items-start'>
        <div className='flex min-w-0 flex-1 flex-col gap-6'>
          {/* 워크보드 Scenario 페이지의 "The situation" (briefs.scenario) */}
          <Field label='The situation' htmlFor='ops-scenario'>
            <TextArea
              id='ops-scenario'
              readOnly={locked}
              className={cn(locked && 'border-transparent bg-green-deep03/[0.04]')}
              placeholder='§1 상황 요약'
              value={draft.scenario}
              onChange={(e) => setDraft((d) => ({ ...d, scenario: e.target.value }))}
            />
          </Field>
          {/* 워크보드 Scenario 페이지의 "What to build" (briefs.deliverable) */}
          <Field label='What to build' htmlFor='ops-deliverable'>
            <TextArea
              id='ops-deliverable'
              readOnly={locked}
              className={cn(locked && 'border-transparent bg-green-deep03/[0.04]')}
              placeholder='§3 만들 것 요약'
              value={draft.deliverable}
              onChange={(e) => setDraft((d) => ({ ...d, deliverable: e.target.value }))}
            />
          </Field>

          <div className='flex flex-col gap-3'>
            <span className='text-[13px] font-medium text-text-primary'>Jobs</span>
            <div className='flex flex-wrap gap-2'>
              {JOBS.map((j) => {
                const on = draft.jobs.includes(j.key);
                return (
                  <button
                    key={j.key}
                    type='button'
                    aria-pressed={on}
                    aria-disabled={locked}
                    onClick={() => toggle('jobs', j.key)}
                    className={cn(
                      'flex h-10 items-center gap-1.5 rounded-full border px-4 text-[14px] transition-colors',
                      locked && 'cursor-default hover:bg-transparent',
                      on
                        ? 'border-green-deep02 font-medium text-green-deep04'
                        : 'border-green-deep03/20 text-text-primary hover:bg-grayscale-gray01',
                    )}
                  >
                    {on && <CheckIcon className='h-3.5 w-3.5' />}
                    {j.label}
                    {j.optional && (
                      <span className='text-[12px] font-normal text-text-secondary'>Optional</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className='flex flex-col gap-3'>
            <span className='text-[13px] font-medium text-text-primary'>Handoff types</span>
            <ul className='divide-y divide-green-deep03/[0.08] rounded-xl border border-green-deep03/[0.08] bg-grayscale-gray00 shadow-4'>
              {HANDOFFS.map((h) => (
                <li key={h.key} className='flex items-start gap-3 px-4 py-3'>
                  <Checkbox
                    checked={h.always || draft.handoffs.includes(h.key)}
                    disabled={h.always}
                    onChange={() => toggle('handoffs', h.key)}
                    className={cn('!h-auto !items-start gap-3', locked && '!cursor-default')}
                    boxClassName={cn('mt-0.5 shrink-0', h.always && '!bg-functional-success03/50')}
                    label={
                      <span className='flex flex-col gap-0.5'>
                        <span className='text-[14px] font-medium text-text-primary'>{h.title}</span>
                        <span className='text-[12px] text-text-secondary'>{h.items}</span>
                      </span>
                    }
                  />
                  {h.always && (
                    <span className='ml-auto shrink-0'>
                      <StatusChip chip={{ label: 'Always', tone: 'gray' }} />
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* 워크보드 Resources 에 보이는 파일 — 시작 뒤에도 고칠 수 있다 (Handoff types 와 같은 폭) */}
          <TeamFiles projectId={project.id} mode={mode} toast={api.toast} />
        </div>

        <aside className='rounded-xl bg-green-deep03/[0.04] p-4 text-[13px] leading-[150%] text-text-secondary lg:w-[240px] lg:shrink-0'>
          <p className='mb-2 font-medium text-text-primary'>When you set due times</p>
          <p className='mb-2'>Leave room for one round of rework in every task.</p>
          <p className='mb-2'>Set the last task 2 hours before the project ends.</p>
          <p>Keep the brief small enough to finish in 2 days.</p>
        </aside>
      </div>
    </StepCard>
  );
};

/* ── 3. Team & roles ────────────────────────────────────── */

/** "Oct 9, 2026" */
const formatDate = (iso) =>
  iso
    ? new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'Asia/Seoul',
      }).format(new Date(iso))
    : '';

const CreateTeamModal = ({ onClose, onCreate, existing }) => {
  const [name, setName] = useState('');
  const full = name.trim() && !/^team\s/i.test(name.trim()) ? `Team ${name.trim()}` : name.trim();
  const taken = existing.some((t) => t.name.toLowerCase() === full.toLowerCase());
  return (
    <Modal
      title='Create team'
      onClose={onClose}
      footer={
        <>
          <ModalButton onClick={onClose}>Cancel</ModalButton>
          <ModalButton primary disabled={!full || taken} onClick={() => onCreate(full)}>
            Create team
          </ModalButton>
        </>
      }
    >
      <Field
        label='Team name'
        htmlFor='ops-team-name'
        hint={taken ? 'This name is already used.' : null}
      >
        <TextInput
          id='ops-team-name'
          autoFocus
          placeholder='e.g. Bori'
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
    </Modal>
  );
};

/** + Add member — 초대 링크를 보여 준다. 링크를 연 사람은 Pre-join 을 거쳐 팀에 들어온다 */

/**
 * + Add members — 파일럿 신청자 전체에서 검색해 여러 명을 골라 이 팀에 넣는다.
 * 다른 팀에 이미 들어간 사람(저장 전 편집 포함)은 고를 수 없다.
 */
const AddMembersModal = ({
  team,
  applicants,
  staff,
  takenBy,
  backupOf,
  mode = 'members',
  onClose,
  onAdd,
}) => {
  // backups = 예비 후보 추가 — 신청자만, 고른 순서대로 뒤에 붙는다
  const forBackups = mode === 'backups';
  const [query, setQuery] = useState('');
  const [role, setRole] = useState('');
  const [picked, setPicked] = useState([]);
  const q = query.trim().toLowerCase();
  // 검색·직무 필터는 두 그룹에 같이 적용한다
  const matches = (name, email, job) =>
    (!role || job === role) &&
    (!q || name.toLowerCase().includes(q) || email.toLowerCase().includes(q));
  const staffRows = forBackups ? [] : staff.filter((s) => matches(s.name, s.email, s.job));
  const applicantRows = applicants.filter((a) => matches(a.fullName, a.email, a.role));
  const inThisTeam = (staffId) => team.members.some((m) => m.staff && m.staffId === staffId);
  const toggle = (key) =>
    setPicked((xs) => (xs.includes(key) ? xs.filter((x) => x !== key) : [...xs, key]));

  const Row = ({ k, name, sub, job, isStaff, disabledNote, right }) => {
    const on = picked.includes(k);
    return (
      <li className={cn('flex items-center gap-3 px-4 py-2.5', disabledNote && 'opacity-50')}>
        <Checkbox
          checked={on}
          disabled={!!disabledNote}
          onChange={() => !disabledNote && toggle(k)}
          className={cn(disabledNote && '!cursor-not-allowed')}
        />
        <span className='flex min-w-0 flex-1 flex-col'>
          <span className='flex min-w-0 items-center gap-2'>
            <span className='truncate text-[14px] font-medium text-text-primary'>{name}</span>
            {isStaff && <StatusChip chip={{ label: 'Staff', tone: 'outline' }} />}
          </span>
          <span className='truncate text-[12px] text-text-secondary'>{sub}</span>
        </span>
        <span className='w-[84px] shrink-0 text-[13px] text-text-primary'>{jobLabel(job)}</span>
        <span className='w-[110px] shrink-0 text-[12px] text-text-secondary'>
          {disabledNote ?? right}
        </span>
      </li>
    );
  };
  const groupTitle =
    'border-b border-green-deep03/[0.06] bg-green-deep03/[0.03] px-4 py-2 text-[12px] font-medium text-text-secondary';

  return (
    <Modal
      title={forBackups ? `Add backup candidates to ${team.name}` : `Add members to ${team.name}`}
      subtitle={
        forBackups
          ? 'Pick them in the order you’d ask them. They’re added to the end of the list.'
          : 'Add staff accounts or people from the pilot applications.'
      }
      onClose={onClose}
      width='max-w-[720px]'
      footer={
        <>
          <ModalButton onClick={onClose}>Cancel</ModalButton>
          <ModalButton
            primary
            disabled={picked.length === 0}
            onClick={() =>
              onAdd({
                staff: staff.filter((s) => picked.includes(`s-${s.id}`)),
                // 고른 순서대로 (예비 후보 순번)
                applicants: picked
                  .filter((k) => k.startsWith('a-'))
                  .map((k) => applicants.find((a) => `a-${a.applicationId}` === k))
                  .filter(Boolean),
              })
            }
          >
            {picked.length ? `Add ${picked.length}` : 'Add'}
          </ModalButton>
        </>
      }
    >
      <div className='flex flex-col gap-2 sm:flex-row'>
        <TextInput
          autoFocus
          aria-label='Search staff and applicants'
          placeholder='Search by name or email'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Select
          aria-label='Role'
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className='sm:w-[160px]'
        >
          <option value=''>All roles</option>
          {JOBS.map((j) => (
            <option key={j.key} value={j.key}>
              {j.label}
            </option>
          ))}
        </Select>
      </div>
      <div className='max-h-[420px] overflow-y-auto rounded-xl border border-green-deep03/[0.08] shadow-4'>
        {/* 스태프 — 신청서 없이 들어온다. 여러 팀에 들어갈 수 있다 */}
        {!forBackups && <p className={groupTitle}>Staff · no application needed</p>}
        {!forBackups &&
          (staffRows.length === 0 ? (
            <p className='px-4 py-4 text-[13px] text-text-secondary'>
              {staff.length === 0 ? 'No staff accounts yet.' : 'No staff match your search.'}
            </p>
          ) : (
            <ul className='divide-y divide-green-deep03/[0.08]'>
              {staffRows.map((s) => (
                <Row
                  key={`s-${s.id}`}
                  k={`s-${s.id}`}
                  name={s.name}
                  sub={s.email}
                  job={s.job}
                  isStaff
                  disabledNote={inThisTeam(s.id) ? 'In this team' : null}
                  right='Staff account'
                />
              ))}
            </ul>
          ))}
        <p className={cn(groupTitle, !forBackups && 'border-t border-green-deep03/[0.06]')}>
          Applicants
        </p>
        {applicantRows.length === 0 ? (
          <p className='px-4 py-4 text-[13px] text-text-secondary'>
            {applicants.length === 0 ? 'No applications yet.' : 'No one matches your search.'}
          </p>
        ) : (
          <ul className='divide-y divide-green-deep03/[0.08]'>
            {applicantRows.map((a) => {
              const taken = takenBy(a.applicationId);
              const backup = backupOf?.(a.applicationId);
              return (
                <Row
                  key={`a-${a.applicationId}`}
                  k={`a-${a.applicationId}`}
                  name={a.fullName}
                  sub={a.email}
                  job={a.role}
                  disabledNote={taken ? `In ${taken}` : backup ? `Backup · ${backup}` : null}
                  right={
                    forBackups && picked.includes(`a-${a.applicationId}`)
                      ? `#${picked.filter((k) => k.startsWith('a-')).indexOf(`a-${a.applicationId}`) + 1} in order`
                      : formatDate(a.appliedAt)
                  }
                />
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
};

const MEMBER_COLS =
  'grid grid-cols-[minmax(140px,1fr)_minmax(180px,1.2fr)_96px_100px_110px_60px] items-center gap-4';

const TeamLink = ({ slug }) => {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/2days/join/${slug}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt('Copy this link', url);
    }
  };
  return (
    <div className='flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-green-deep03/[0.08] px-4 py-2.5'>
      <span className='w-[88px] shrink-0 text-[12px] font-medium text-text-secondary'>
        Team link
      </span>
      <span className='min-w-0 flex-1 truncate text-[13px] text-text-primary'>{url}</span>
      <TdButton variant='ghost' size='s' onClick={copy} className='shrink-0'>
        {copied ? 'Link copied' : 'Copy link'}
      </TdButton>
      <span className='basis-full pl-[100px] text-[12px] text-text-secondary max-sm:pl-0'>
        Send this to the members you picked. They log in with the email they applied with.
      </span>
    </div>
  );
};

const TeamWarnings = ({ items }) => {
  const shown = items.filter(Boolean);
  if (shown.length === 0) return null;
  return (
    <ul className='mx-4 mt-3 flex flex-col gap-1 rounded-xl border border-[#ffdf56] bg-functional-warning01 px-4 py-3'>
      {shown.map((text) => (
        <li
          key={text}
          className='flex items-center gap-2.5 text-[14px] font-medium text-functional-warning04'
        >
          <span className='h-1.5 w-1.5 shrink-0 rounded-full bg-functional-warning03' />
          {text}
        </li>
      ))}
    </ul>
  );
};

/** 노란 경고 상자 — 진행 중 변경의 영향 */
export const WarnBox = ({ items, className }) => {
  const shown = items.filter(Boolean);
  if (shown.length === 0) return null;
  return (
    <ul
      className={cn(
        'flex flex-col gap-1.5 rounded-xl border border-[#ffdf56] bg-functional-warning01 px-4 py-3',
        className,
      )}
    >
      {shown.map((text) => (
        <li
          key={text}
          className='flex items-start gap-2.5 text-[13px] leading-[150%] text-functional-warning04'
        >
          <span className='mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-functional-warning03' />
          {text}
        </li>
      ))}
    </ul>
  );
};

const HOUR = 60 * 60 * 1000;

/**
 * 팀원 상태 — 이탈 · 합류 · (신청자) 선발 안내 응답.
 * 선발 안내 메일 → 20시간 뒤 리마인드 1회 → 24시간 안에 팀 링크로 들어오지 않으면 No response (다음 후보 검토)
 */
const memberChip = (m, now) => {
  if (m.status === 'dropped') return { label: 'Dropped', tone: 'failed' };
  if (m.joinedAt) return { label: 'Joined', tone: 'success' };
  if (m.staff) return { label: 'Not joined', tone: 'gray' };
  if (m.linkOpenedAt) return { label: 'Opened link', tone: 'newRequest' };
  if (!m.invitedAt) return { label: 'Invite not sent', tone: 'failed' };
  if (now - new Date(m.invitedAt) >= 24 * HOUR) return { label: 'No response', tone: 'warning' };
  return { label: m.remindedAt ? 'Reminded' : 'Invited', tone: 'gray' };
};
const inviteTitle = (m) =>
  [
    m.invitedAt && `Invited ${formatDue(m.invitedAt, KST)} KST`,
    m.remindedAt && `Reminded ${formatDue(m.remindedAt, KST)} KST`,
    m.linkOpenedAt && `Opened link ${formatDue(m.linkOpenedAt, KST)} KST`,
  ]
    .filter(Boolean)
    .join(' · ') || undefined;

/**
 * 진행 중에는 빼지 못하고 이탈 처리만 하는 팀원 — 합류한 신청자, 댓글·체크인을 남긴 사람 (서버 규칙과 같다).
 * 태스크만 있는 사람(무응답 등)은 뺄 수 있고, 태스크는 같은 직무의 다른 사람에게 넘어간다
 */
const mustKeep = (m) =>
  m.id != null && (m.status === 'dropped' || (!m.staff && !!m.joinedAt) || !!m.hasActivity);

const memberKey = (m) => (m.staff ? `s${m.staffId}` : `a${m.applicationId}`);

/** 저장 전 바뀐 것 — 진행 중 저장 확인에 보여준다 */
const teamChanges = (teams, initial) => {
  const lines = [];
  teams.forEach((t) => {
    const before = initial.find((x) => x.id === t.id);
    if (!before) return;
    if (before.name.trim() !== t.name.trim())
      lines.push(`Rename “${before.name}” to “${t.name.trim()}”`);
    const was = new Set(before.members.map(memberKey));
    const now = new Set(t.members.map(memberKey));
    t.members
      .filter((m) => !was.has(memberKey(m)))
      .forEach((m) => lines.push(`Add ${m.name} to ${t.name.trim()}`));
    before.members
      .filter((m) => !now.has(memberKey(m)))
      .forEach((m) => lines.push(`Remove ${m.name} from ${before.name}`));
    const order = (list) => (list ?? []).map((b) => b.applicationId).join(',');
    if (order(before.backups) !== order(t.backups)) {
      lines.push(
        `Backups for ${t.name.trim()}: ${(t.backups ?? []).map((b, i) => `#${i + 1} ${b.name}`).join(', ') || 'none'}`,
      );
    }
  });
  return lines;
};

/** 이탈 판정 · 되돌리기 — 운영진만. 사유를 남기고, 기록은 지우지 않는다 */
const DropModal = ({ target, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const { member: m, team, leader } = target;
  const undo = m.status === 'dropped';
  const confirm = async () => {
    setBusy(true);
    const ok = await onConfirm(undo ? 'active' : 'dropped', reason.trim());
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Modal
      title={undo ? `Undo dropout for ${m.name}?` : `Mark ${m.name} as dropped?`}
      subtitle={
        undo
          ? 'Use this only if the dropout was a mistake.'
          : 'Only ops can mark a dropout. Their records stay on the board.'
      }
      onClose={onClose}
      footer={
        <>
          <ModalButton onClick={onClose}>Cancel</ModalButton>
          <TdButton
            variant={undo ? 'primary' : 'danger'}
            disabled={busy || (!undo && !reason.trim())}
            onClick={confirm}
          >
            {undo ? 'Undo dropout' : 'Mark dropped'}
          </TdButton>
        </>
      }
    >
      <WarnBox
        items={
          undo
            ? [
                `${m.name} can open and act on the ${team.name} board again.`,
                'Tasks you already reassigned stay with their new owners.',
              ]
            : [
                `${m.name} can no longer open the board. They see a blocked page with your reason.`,
                'Their open tasks stay with them. Reassign each one in Tasks.',
                'Handoffs they would receive need a new receiver in Tasks.',
                m === leader &&
                  `${team.name} will have no leader. Nobody takes over the PM role automatically.`,
                'This is recorded as a dropout. It isn’t an automatic penalty.',
              ]
        }
      />
      {!undo && (
        <Field
          label='Reason'
          required
          htmlFor='drop-reason'
          hint='Kept with the dropout record and shown to the member on the blocked page.'
        >
          <TextArea
            id='drop-reason'
            value={reason}
            maxLength={500}
            placeholder='What happened? e.g. No response to 2 ops calls during working hours'
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      )}
    </Modal>
  );
};

/** 예비 후보 순번 — 선발한 사람이 24시간 안에 응답하지 않으면 앞에서부터 검토한다 */
const BackupList = ({ team, locked, onAdd, onMove, onRemove, onPromote }) => {
  const list = team.backups ?? [];
  return (
    <div className='border-t border-green-deep03/[0.08]'>
      <div className='flex items-center gap-3 bg-green-deep03/[0.03] px-4 py-2.5'>
        <span className='text-[13px] font-medium text-text-primary'>Backup candidates</span>
        <span className='text-[12px] text-text-secondary'>
          {list.length ? `${list.length} in order` : 'None yet'}
        </span>
        {!locked && (
          <TdButton variant='ghost' size='s' onClick={onAdd} className='ml-auto'>
            + Add backups
          </TdButton>
        )}
      </div>
      {list.length === 0 ? (
        <p className='px-4 py-4 text-[13px] text-text-secondary'>
          Add people in the order you’d ask them if a selected member doesn’t respond within 24
          hours.
        </p>
      ) : (
        <ol>
          {list.map((b, i) => (
            <li
              key={b.applicationId}
              className='flex min-h-11 items-center gap-3 border-t border-green-deep03/[0.06] px-4 py-2 text-[14px]'
            >
              <span className='w-6 shrink-0 text-[13px] font-medium text-green-deep04'>
                #{i + 1}
              </span>
              <span className='min-w-0 flex-1 truncate font-medium text-text-primary'>
                {b.name}
              </span>
              <span className='hidden min-w-0 flex-1 truncate text-[13px] text-text-secondary sm:block'>
                {b.email}
              </span>
              <span className='w-[84px] shrink-0 text-[13px] text-text-primary'>
                {jobLabel(b.job)}
              </span>
              {!locked && (
                <span className='flex shrink-0 items-center gap-1'>
                  <button
                    type='button'
                    aria-label='Move up'
                    disabled={i === 0}
                    onClick={() => onMove(i, -1)}
                    className='flex h-7 w-7 items-center justify-center rounded-md text-text-secondary hover:bg-grayscale-gray01 disabled:opacity-30'
                  >
                    <ArrowIcon className='h-3.5 w-3.5 -rotate-90' />
                  </button>
                  <button
                    type='button'
                    aria-label='Move down'
                    disabled={i === list.length - 1}
                    onClick={() => onMove(i, 1)}
                    className='flex h-7 w-7 items-center justify-center rounded-md text-text-secondary hover:bg-grayscale-gray01 disabled:opacity-30'
                  >
                    <ArrowIcon className='h-3.5 w-3.5 rotate-90' />
                  </button>
                  <button
                    type='button'
                    onClick={() => onPromote(b)}
                    className='ml-1 text-[12px] font-medium text-green-deep03 hover:underline'
                  >
                    Add to team
                  </button>
                  <button
                    type='button'
                    onClick={() => onRemove(b)}
                    className='ml-1 text-[12px] text-text-secondary hover:text-functional-failed04'
                  >
                    Remove
                  </button>
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

/** 같은 직무의 첫 예비 후보 */
const nextBackup = (team, job) => (team.backups ?? []).find((b) => b.job === job) ?? null;

export const TeamsStep = ({ project, dispatch, api, onSaved }) => {
  const initial = project.teams;
  const [teams, setTeams] = useDraft(initial);
  const [creating, setCreating] = useState(false);
  const [addingTo, setAddingTo] = useState(null);
  const [applicants, setApplicants] = useState(null);
  const [saving, runSave] = useSaving();
  const mode = setupMode(project);
  // 시작 뒤에도 운영진은 팀 이름·팀원을 바꾼다 — 끝나면 읽기 전용
  // 시작 뒤(끝난 뒤 포함) — 합류했거나 기록이 있는 팀원은 빼지 못한다. 새 팀은 더할 수 있다
  const live = mode !== 'draft';
  const locked = false;
  const [dropping, setDropping] = useState(null);
  const [confirmSave, setConfirmSave] = useState(false);
  // 팀은 만들자마자 서버에 저장된다 — 고치던 내용이 있어도 새 팀은 목록에 붙인다
  const missing = initial.filter((t) => !teams.some((x) => x.id === t.id));
  if (missing.length)
    setTeams((ts) => [...ts, ...missing.filter((t) => !ts.some((x) => x.id === t.id))]);
  const dirty = !same(teams, initial);
  useReportDirty(dispatch, 'teams', dirty);

  const updateTeam = (id, fn) => setTeams((ts) => ts.map((t) => (t.id === id ? fn(t) : t)));
  const updateMember = (teamId, applicationId, patch) =>
    updateTeam(teamId, (t) => ({
      ...t,
      members: t.members.map((m) =>
        m.applicationId === applicationId ? { ...m, ...patch(m) } : m,
      ),
    }));
  /** 이 신청자가 들어간 팀 이름 (편집 중인 상태 기준) */
  const takenBy = (applicationId) =>
    teams.find((t) => t.members.some((m) => m.applicationId === applicationId))?.name ?? null;

  const [staffList, setStaffList] = useState([]);
  const [addingMode, setAddingMode] = useState('members');
  /** 이 신청자가 예비 후보로 있는 팀 이름 */
  const backupOf = (applicationId) =>
    teams.find((t) => (t.backups ?? []).some((b) => b.applicationId === applicationId))?.name ??
    null;
  const moveBackup = (teamId, i, dir) =>
    updateTeam(teamId, (t) => {
      const list = [...(t.backups ?? [])];
      [list[i], list[i + dir]] = [list[i + dir], list[i]];
      return { ...t, backups: list };
    });
  const removeBackup = (teamId, b) =>
    updateTeam(teamId, (t) => ({
      ...t,
      backups: (t.backups ?? []).filter((x) => x.applicationId !== b.applicationId),
    }));
  /** 예비 후보를 팀원으로 — replace 가 있으면 그 사람을 빼고 넣는다 (저장하면 선발 메일) */
  const promote = (teamId, b, replace) =>
    updateTeam(teamId, (t) => ({
      ...t,
      members: [
        ...t.members.filter(
          (x) => !replace || x.staff || x.applicationId !== replace.applicationId,
        ),
        {
          id: null,
          applicationId: b.applicationId,
          name: b.name,
          email: b.email,
          job: b.job,
          staffBackup: false,
          appliedAt: b.appliedAt,
        },
      ],
      backups: (t.backups ?? []).filter((x) => x.applicationId !== b.applicationId),
    }));
  const openAdd = async (team, mode = 'members') => {
    setAddingMode(mode);
    setAddingTo(team);
    const [a, s] = await Promise.all([
      api.getApplicants().catch(() => []),
      getOpsStaff().catch(() => []),
    ]);
    setStaffList(s ?? []);
    setApplicants(a ?? []);
  };
  // 팀 이름은 비지 않고 겹치지 않는다
  const nameError = (team) => {
    const name = team.name.trim();
    if (!name) return 'Enter a team name';
    const dup = teams.some(
      (t) => t.id !== team.id && t.name.trim().toLowerCase() === name.toLowerCase(),
    );
    return dup ? 'Another team has this name' : null;
  };
  const namesOk = teams.every((t) => !nameError(t));
  const doSave = () =>
    runSave(
      () => api.saveTeams(teams.map((t) => ({ ...t, name: t.name.trim() }))),
      () => {
        setConfirmSave(false);
        onSaved();
      },
    );
  // 진행 중이면 무엇이 바뀌는지 보여주고 한 번 더 묻는다
  const changes = teamChanges(teams, initial);
  // 빼는 사람 중 워크보드 태스크가 걸린 사람
  const removedWithTasks = initial.flatMap((before) => {
    const now = teams.find((t) => t.id === before.id);
    const keep = new Set((now?.members ?? []).map(memberKey));
    return before.members.filter((m) => m.hasRecords && !keep.has(memberKey(m)));
  });
  // 새로 선발한 신청자 — 저장하면 바로 선발 안내 메일이 간다
  const invites = teams.reduce(
    (n, t) => n + t.members.filter((m) => !m.staff && m.id == null).length,
    0,
  );
  // 진행 중이거나 메일이 나가면 무엇이 바뀌는지 보여주고 한 번 더 묻는다
  const save = () => (live || invites > 0 ? setConfirmSave(true) : doSave());
  const now = useNow();
  const resend = (m) => runSave(() => api.resendInvite(m.id));

  return (
    <StepCard
      title='Team & roles'
      desc='Create teams, then add people from the pilot applications.'
      notice={locked ? lockedNotice(mode) : null}
      warning={
        live
          ? `${liveLead(mode)} Team changes show on the workboard right away. Members who joined or have records can’t be removed — mark them as dropped instead. A new team gets its tasks from Tasks when its workboard first opens.`
          : null
      }
      hint={
        teams.length === 0
          ? 'Create a team first'
          : !namesOk
            ? 'Fix the team names first'
            : project.done.teams && !dirty
              ? 'No changes to save'
              : null
      }
      action={locked ? null : 'Save'}
      disabled={saving || teams.length === 0 || !namesOk || (project.done.teams && !dirty)}
      onAction={save}
    >
      {live && (
        <WarnBox
          items={[
            'This project is running. Saved changes reach team members right away.',
            'You can rename teams, add people and remove people who haven’t joined yet.',
            'If someone you remove has tasks, the tasks move to someone with the same job (a staff account if there’s none).',
            'People who joined or left comments or check-ins can’t be removed or moved. Mark them as dropped instead.',
            'New members need to join with the team link before they can use the board.',
          ]}
        />
      )}
      {teams.length === 0 ? (
        <EmptyBox
          title='No teams yet'
          desc='Create a team, then add members from the applications.'
        >
          {!locked && (
            <TdButton variant='soft' onClick={() => setCreating(true)} className='mt-2'>
              Create team
            </TdButton>
          )}
        </EmptyBox>
      ) : (
        <>
          {teams.map((team) => {
            // 저장 전이라 id 가 없는 팀원도 리더로 보여야 해서 객체로 비교한다
            const leader = leaderOf(team);
            return (
              <div
                key={team.id}
                className='overflow-hidden rounded-xl border border-green-deep03/[0.08] bg-grayscale-gray00 shadow-4'
              >
                <div className='flex items-center gap-3 border-b border-green-deep03/[0.06] bg-green-deep03/[0.03] px-4 py-3'>
                  {locked ? (
                    <span className='text-[15px] font-medium text-text-primary'>{team.name}</span>
                  ) : (
                    // 팀 이름 바꾸기 — 링크(slug)는 그대로
                    <span className='flex flex-col'>
                      <input
                        aria-label='Team name'
                        value={team.name}
                        maxLength={60}
                        onChange={(e) =>
                          updateTeam(team.id, (t) => ({ ...t, name: e.target.value }))
                        }
                        className={cn(
                          '-ml-2 h-8 w-[220px] rounded-md border bg-transparent px-2 text-[15px] font-medium text-text-primary outline-none transition-colors hover:bg-grayscale-gray00 focus:bg-grayscale-gray00',
                          nameError(team)
                            ? 'border-functional-failed03'
                            : 'border-transparent hover:border-green-deep03/[0.2] focus:border-green-deep02',
                        )}
                      />
                      {nameError(team) && (
                        <span className='text-[12px] text-functional-failed04'>
                          {nameError(team)}
                        </span>
                      )}
                    </span>
                  )}
                  <span className='text-[13px] text-text-secondary'>
                    {team.members.length
                      ? `${team.members.length} member${team.members.length > 1 ? 's' : ''}`
                      : 'No members yet'}
                  </span>
                  {!locked && (
                    <TdButton
                      variant='ghost'
                      size='s'
                      onClick={() => openAdd(team)}
                      className='ml-auto'
                    >
                      + Add members
                    </TdButton>
                  )}
                </div>
                <TeamWarnings
                  items={[
                    // 받을 사람이 비는 직무를 메울 스태프가 팀마다 한 명은 있어야 한다
                    !team.members.some((m) => m.staff) &&
                      'Add at least one staff member to this team.',
                    // PM 이 없으면 리더 없음 — 다른 직무를 대신 세우지 않는다
                    !leader && `${team.name} has no leader until a PM is added.`,
                    // 선발 24시간 안에 팀 링크로 들어오지 않은 사람 — 다음 후보 검토
                    (() => {
                      const n = team.members.filter(
                        (m) => m.id != null && memberChip(m, now).label === 'No response',
                      ).length;
                      if (n === 0) return false;
                      const first = team.members.find(
                        (m) => m.id != null && memberChip(m, now).label === 'No response',
                      );
                      const next = nextBackup(team, first.job);
                      return `${n} selected member${n > 1 ? 's' : ''} didn’t open the team link within 24 hours. ${
                        next
                          ? `Next ${jobLabel(first.job)} backup: ${next.name}.`
                          : `No ${jobLabel(first.job)} backup yet — add one below.`
                      }`;
                    })(),
                  ]}
                />
                {team.slug && <TeamLink slug={team.slug} />}
                <div className='overflow-x-auto'>
                  <div className='min-w-[930px]'>
                    <div
                      className={cn(
                        MEMBER_COLS,
                        'px-4 py-2.5 text-[12px] font-medium text-text-secondary',
                      )}
                    >
                      <span>Full name</span>
                      <span>Email</span>
                      <span>Role</span>
                      <span>Staff backup</span>
                      <span>Applied</span>
                      <span />
                    </div>
                    {team.members.length === 0 ? (
                      <p className='border-t border-green-deep03/[0.08] px-4 py-6 text-center text-[14px] text-text-secondary'>
                        No members yet. Add people from the pilot applications.
                      </p>
                    ) : (
                      team.members.map((m) => (
                        <div
                          key={m.staff ? `staff-${m.staffId}` : m.applicationId}
                          className={cn(
                            MEMBER_COLS,
                            'min-h-12 border-t border-green-deep03/[0.08] px-4 py-2 text-[14px] text-text-primary',
                          )}
                        >
                          <span className='flex min-w-0 items-center gap-2'>
                            <span className='truncate font-medium'>{m.name}</span>
                            {/* 받을 직무의 팀원이 없어 자동으로 들어온 운영진 계정 */}
                            {m.staff && <StatusChip chip={{ label: 'Staff', tone: 'outline' }} />}
                            {m === leader && (
                              <StatusChip chip={{ label: 'Leader', tone: 'newRequest' }} />
                            )}
                            {m.id != null && (live || !m.staff) && (
                              <span title={inviteTitle(m)} className='flex shrink-0'>
                                <StatusChip chip={memberChip(m, now)} />
                              </span>
                            )}
                            {/* 메일이 안 갔거나 24시간 응답이 없으면 다시 보낸다 (24시간을 다시 잰다) */}
                            {!locked &&
                              m.id != null &&
                              ['Invite not sent', 'No response'].includes(
                                memberChip(m, now).label,
                              ) && (
                                <button
                                  type='button'
                                  disabled={dirty || saving}
                                  title={
                                    dirty ? 'Save or discard your team changes first' : undefined
                                  }
                                  onClick={() => resend(m)}
                                  className='shrink-0 text-[12px] font-medium text-green-deep03 hover:underline disabled:cursor-not-allowed disabled:opacity-50'
                                >
                                  Resend
                                </button>
                              )}
                            {/* 무응답 — 같은 직무의 다음 예비 후보로 바꾼다 (저장하면 그 사람에게 선발 메일) */}
                            {!locked &&
                              m.id != null &&
                              memberChip(m, now).label === 'No response' &&
                              nextBackup(team, m.job) && (
                                <button
                                  type='button'
                                  disabled={mustKeep(m)}
                                  title={
                                    mustKeep(m)
                                      ? 'They joined or left comments. Mark them dropped, then add the backup from the list.'
                                      : `Replace with ${nextBackup(team, m.job).name}. Their tasks go to them.`
                                  }
                                  onClick={() => promote(team.id, nextBackup(team, m.job), m)}
                                  className='shrink-0 text-[12px] font-medium text-functional-warning04 hover:underline disabled:cursor-not-allowed disabled:opacity-50'
                                >
                                  Replace
                                </button>
                              )}
                          </span>
                          <span className='truncate text-[13px] text-text-secondary'>
                            {m.email}
                          </span>
                          <span className='text-[13px]'>{jobLabel(m.job)}</span>
                          {m.staff ? (
                            // 스태프는 늘 Staff backup — 풀 수 없다
                            <span className='w-fit' title='Staff accounts are always staff backup'>
                              <Checkbox checked onChange={() => {}} className='!cursor-default' />
                            </span>
                          ) : (
                            <Checkbox
                              checked={!!m.staffBackup}
                              className={cn(locked && '!cursor-default')}
                              onChange={() =>
                                !locked &&
                                updateMember(team.id, m.applicationId, (x) => ({
                                  staffBackup: !x.staffBackup,
                                }))
                              }
                            />
                          )}
                          <span className='text-[13px] text-text-secondary'>
                            {m.staff ? '–' : formatDate(m.appliedAt)}
                          </span>
                          {locked ? (
                            <span />
                          ) : live && mustKeep(m) ? (
                            // 합류했거나 기록이 있으면 빼지 않고 이탈 처리 — 저장 안 한 변경이 있으면 먼저 저장
                            <button
                              type='button'
                              disabled={dirty}
                              title={dirty ? 'Save or discard your team changes first' : undefined}
                              onClick={() => setDropping({ member: m, team, leader })}
                              className={cn(
                                'text-left text-[12px] disabled:cursor-not-allowed disabled:opacity-50',
                                m.status === 'dropped'
                                  ? 'text-text-secondary hover:text-text-primary'
                                  : 'text-functional-failed04 hover:underline',
                              )}
                            >
                              {m.status === 'dropped' ? 'Undo dropout' : 'Mark dropped'}
                            </button>
                          ) : (
                            <button
                              type='button'
                              onClick={() =>
                                updateTeam(team.id, (t) => ({
                                  ...t,
                                  members: t.members.filter((x) =>
                                    m.staff
                                      ? !(x.staff && x.staffId === m.staffId)
                                      : x.staff || x.applicationId !== m.applicationId,
                                  ),
                                }))
                              }
                              className='text-[12px] text-text-secondary hover:text-functional-failed04'
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
                <BackupList
                  team={team}
                  locked={locked}
                  onAdd={() => openAdd(team, 'backups')}
                  onMove={(i, dir) => moveBackup(team.id, i, dir)}
                  onRemove={(b) => removeBackup(team.id, b)}
                  onPromote={(b) => promote(team.id, b, null)}
                />
              </div>
            );
          })}
          {/* 시작 뒤에도 새 팀을 만든다 — 태스크는 Tasks 에서 정한다 */}
          {!locked && (
            <TdButton variant='soft' onClick={() => setCreating(true)} className='w-fit'>
              + Create team
            </TdButton>
          )}
        </>
      )}

      {creating && (
        <CreateTeamModal
          existing={teams}
          onClose={() => setCreating(false)}
          onCreate={async (name) => {
            if (await api.createTeam(name)) setCreating(false);
          }}
        />
      )}
      {addingTo && applicants && (
        <AddMembersModal
          team={teams.find((t) => t.id === addingTo.id) ?? addingTo}
          applicants={applicants}
          staff={staffList}
          takenBy={takenBy}
          backupOf={addingMode === 'backups' ? backupOf : null}
          mode={addingMode}
          onClose={() => setAddingTo(null)}
          onAdd={(picked) => {
            if (addingMode === 'backups') {
              updateTeam(addingTo.id, (t) => ({
                ...t,
                backups: [
                  ...(t.backups ?? []),
                  ...picked.applicants.map((a) => ({
                    applicationId: a.applicationId,
                    name: a.fullName,
                    email: a.email,
                    job: a.role,
                    appliedAt: a.appliedAt,
                  })),
                ],
              }));
              setAddingTo(null);
              return;
            }
            // 예비 후보였던 사람을 바로 팀원으로 넣으면 예비 명단에서 뺀다
            const ids = picked.applicants.map((a) => a.applicationId);
            setTeams((ts) =>
              ts.map((t) => ({
                ...t,
                backups: (t.backups ?? []).filter((b) => !ids.includes(b.applicationId)),
              })),
            );
            updateTeam(addingTo.id, (t) => ({
              ...t,
              members: [
                ...t.members,
                ...picked.staff.map((st) => ({
                  id: null,
                  applicationId: null,
                  staffId: st.id,
                  staff: true,
                  name: st.name,
                  email: st.email,
                  job: st.job,
                  staffBackup: true,
                  appliedAt: null,
                })),
                ...picked.applicants.map((a) => ({
                  id: null,
                  applicationId: a.applicationId,
                  name: a.fullName,
                  email: a.email,
                  job: a.role,
                  staffBackup: false,
                  appliedAt: a.appliedAt,
                })),
              ],
            }));
            setAddingTo(null);
          }}
        />
      )}
      {dropping && (
        <DropModal
          target={dropping}
          onClose={() => setDropping(null)}
          onConfirm={(status, reason) =>
            api.setMemberStatus(dropping.member.id, status, reason || null)
          }
        />
      )}
      {confirmSave && (
        <Modal
          title={live ? 'Save changes to a running project?' : 'Send selection emails?'}
          subtitle={
            live
              ? 'Team members see these changes right away.'
              : 'Saving sends the selection email right away.'
          }
          onClose={() => setConfirmSave(false)}
          footer={
            <>
              <ModalButton onClick={() => setConfirmSave(false)}>Cancel</ModalButton>
              <ModalButton primary disabled={saving} onClick={doSave}>
                {invites > 0 && !live ? 'Save and send' : 'Save changes'}
              </ModalButton>
            </>
          }
        >
          <ul className='flex flex-col gap-1.5 text-[14px] text-text-primary'>
            {(changes.length ? changes : ['Staff backup settings']).map((c) => (
              <li key={c} className='flex gap-2'>
                <span aria-hidden className='text-text-secondary'>
                  ·
                </span>
                {c}
              </li>
            ))}
          </ul>
          <WarnBox
            items={[
              invites > 0 &&
                `${invites} new member${invites > 1 ? 's get' : ' gets'} a selection email with the team link. They have 24 hours to open it, and get one reminder after 20 hours.`,
              live &&
                changes.some((c) => c.startsWith('Add')) &&
                'New members need to join with the team link before they can use the board.',
              // 태스크가 있던 사람을 빼면 같은 직무의 다른 사람에게 넘어간다
              live &&
                removedWithTasks.length > 0 &&
                `Tasks of ${removedWithTasks.map((m) => m.name).join(', ')} move to someone with the same job (a staff account if there’s none). They have to accept them again.`,
              changes.some((c) => c.startsWith('Rename')) &&
                'The team link stays the same. Members see the new name on their board.',
            ]}
          />
        </Modal>
      )}
    </StepCard>
  );
};

/* ── 4. Tasks ───────────────────────────────────────────── */

/** 이름 + Staff 배지 — 팀에 그 직무 팀원이 없어 스태프가 대신 맡거나 받을 때 */
const PersonName = ({ person, auto }) => (
  <span className='inline-flex items-center gap-1.5'>
    <span>{person.name}</span>
    {person.staff && <StatusChip chip={{ label: 'Staff', tone: 'outline' }} />}
    {auto && <span className='text-text-placeholder'>· auto</span>}
  </span>
);

const memberLabel = (m) =>
  m.id === NPC_LEAD.id ? m.name : `${m.name} · ${jobLabel(m.job)}${m.staff ? ' · Staff' : ''}`;

const templateBaseOf = (t) =>
  t?.role
    ? baseItemsOf(handoffTypeByJobs(t.role, t.defaultReceiverRole ?? null, !t.defaultReceiverRole))
    : [];

const templateItemCount = (t) => {
  const base = templateBaseOf(t);
  return base.length + (t.taskList ?? []).filter((x) => !base.includes(x)).length;
};

/**
 * 태스크 추가·고치기 (task 를 넘기면 고치기). 필드는 기획서 task_templates 와 같다.
 * 마감은 T0(리더 시작) + N시간으로 넣는다 — 실제 마감은 PM 이 사전 1시간에 확정한다.
 * 선행 태스크는 이 태스크보다 앞에 있는 태스크만 고를 수 있다.
 */
const AddTaskModal = ({ team, project, task, list, staffList = [], onClose, onAdd }) => {
  const editing = !!task;
  const [title, setTitle] = useState(task?.title ?? '');
  // 직무는 필수, 담당자는 선택 — 비워 두면 그 직무의 팀원이 정해질 때 자동 배정된다
  const [role, setRole] = useState(task?.role ?? '');
  const [assigneeId, setAssigneeId] = useState(task?.assigneeId ?? '');
  const [offset, setOffset] = useState(
    task?.dueOffsetHours != null ? String(task.dueOffsetHours) : '',
  );
  const [baseline, setBaseline] = useState(
    task?.baselineMinutes ? String(task.baselineMinutes / 60) : '',
  );
  const [dependsOn, setDependsOn] = useState(task?.dependsOn ?? []);
  const [items, setItems] = useState(() => {
    const base = templateBaseOf(task);
    const own = (task?.taskList ?? []).filter((x) => !base.includes(x));
    return own.length ? own : [''];
  });
  const [tags, setTags] = useState(task?.skillTags ?? []);
  // 받는 사람을 직접 고른 경우 (같은 직무가 여럿일 때) — 비우면 규칙으로 자동
  const [receiverPick, setReceiverPick] = useState(
    task?.receiverPinned && task.receiverId != null ? String(task.receiverId) : '',
  );
  const candidates = team.members.filter((m) => !m.staff && m.job === role);
  const assignee = candidates.find((m) => String(m.id) === String(assigneeId)) ?? null;
  // 받는 직무 — 고치는 태스크는 그대로, 새 태스크는 인계 순서로 (최종 제출 P2 는 팀장)
  const receiverRole = editing
    ? role === task.role
      ? (task.defaultReceiverRole ?? null)
      : receiverRoleFor(role)
    : receiverRoleFor(role);
  const autoReceiver = receiverPreview(
    team.members,
    task?.code === 'P2' ? null : receiverRole,
    staffList,
  );
  // 고를 수 있는 받는 사람 — 받는 직무의 같은 팀 사람(스태프 포함, 이탈 · 담당자 제외)
  const receiverCandidates =
    task?.code === 'P2' || !receiverRole
      ? []
      : team.members.filter(
          (m) =>
            m.job === receiverRole &&
            m.status !== 'dropped' &&
            String(m.id) !== String(assignee?.id ?? ''),
        );
  const pickedReceiver = receiverCandidates.find((m) => String(m.id) === receiverPick) ?? null;
  const receiver = pickedReceiver ?? autoReceiver;
  // 담당자를 비워 두면 그 직무의 팀원 → 스태프가 맡는다 (둘 다 없으면 Unassigned)
  const fallback = personForRole(team.members, role, staffList);
  const index = editing ? list.findIndex((t) => t.id === task.id) : list.length;
  const before = list.slice(0, index);

  const offsetNum = Number(offset);
  const offsetOk =
    offset !== '' && Number.isInteger(offsetNum) && offsetNum >= 2 && offsetNum <= LAST_DUE_H;
  const baselineMin = baseline === '' ? null : Math.round(Number(baseline) * 60);
  const baselineOk = baseline === '' || (baselineMin > 0 && baselineMin <= 2880);
  const baseType = role
    ? handoffTypeByJobs(
        role,
        task?.code === 'P2' ? null : receiverRole,
        task?.code === 'P2' || !receiverRole,
      )
    : null;
  const base = baseItemsOf(baseType);
  const cleanItems = items
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((x) => !base.includes(x));
  const tagOptions = SKILL_TAGS[role] ?? [];
  const ok = title.trim() && role && offsetOk && baselineOk && base.length + cleanItems.length > 0;
  const hint = !role
    ? 'Choose a role'
    : offset !== '' && !offsetOk
      ? `Deadline must be T0 + 2 to ${LAST_DUE_H} hours`
      : !baselineOk
        ? 'Estimated time must be between 0 and 48 hours'
        : 'Add a deadline and at least one Task List item';

  return (
    <Modal
      title={editing ? `Edit task · ${task.code}` : 'Add task'}
      subtitle={team.name}
      onClose={onClose}
      width='max-w-[560px]'
      footer={
        <div className='flex w-full items-center justify-end gap-3'>
          {!ok && (
            <span className='mr-auto text-[13px] leading-[140%] text-text-secondary'>{hint}</span>
          )}
          <ModalButton onClick={onClose}>Cancel</ModalButton>
          <ModalButton
            primary
            disabled={!ok}
            onClick={() =>
              onAdd({
                ...(task ?? {}),
                id: task?.id ?? `task-${Date.now()}`,
                code: task?.code ?? nextTaskCode(role, list),
                title: title.trim(),
                role,
                assigneeId: assignee?.id ?? null,
                defaultReceiverRole: task?.code === 'P2' ? null : receiverRole,
                receiverId: receiver.id,
                receiverPinned: !!pickedReceiver,
                baselineMinutes: baselineMin,
                dueOffsetHours: offsetNum,
                dueAt: addHours(project.leaderStartAt, offsetNum),
                dependsOn: dependsOn.filter((c) => before.some((t) => t.code === c)),
                taskList: cleanItems,
                skillTags: tags.filter((t) => tagOptions.includes(t)),
              })
            }
          >
            {editing ? 'Save task' : 'Add task'}
          </ModalButton>
        </div>
      }
    >
      <Field label='Task' htmlFor='ops-task-title'>
        <TextInput
          id='ops-task-title'
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </Field>
      <div className='grid gap-4 sm:grid-cols-2'>
        <Field label='Role' required htmlFor='ops-task-role'>
          <Select
            id='ops-task-role'
            value={role}
            onChange={(e) => {
              setRole(e.target.value);
              setAssigneeId('');
              setReceiverPick('');
              setTags([]);
            }}
          >
            {!role && <option value=''>Choose a role</option>}
            {JOBS.map((j) => (
              <option key={j.key} value={j.key}>
                {j.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label='Assignee'
          optional
          htmlFor='ops-task-assignee'
          hint={
            assignee
              ? null
              : 'Unassigned is fine — the member with this role is assigned automatically.'
          }
        >
          <Select
            id='ops-task-assignee'
            value={assignee ? assignee.id : ''}
            disabled={!role}
            onChange={(e) => setAssigneeId(e.target.value)}
          >
            <option value=''>
              {fallback
                ? `Auto — ${fallback.name}${fallback.staff ? ' · Staff' : ''}`
                : 'Unassigned (auto)'}
            </option>
            {candidates.map((m) => (
              <option key={m.id} value={m.id}>
                {memberLabel(m)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className='grid gap-4 sm:grid-cols-2'>
        <Field
          label='Suggested deadline'
          htmlFor='ops-task-offset'
          hint={
            offsetOk
              ? `${formatKst(addHours(project.leaderStartAt, offsetNum), { year: false })} KST. The PM confirms the real deadline.`
              : 'Hours after T0 (leader start).'
          }
        >
          <div className='flex items-center gap-2 text-[14px] text-text-secondary'>
            <span className='shrink-0'>T0 +</span>
            <TextInput
              id='ops-task-offset'
              type='number'
              inputMode='numeric'
              min={2}
              max={LAST_DUE_H}
              value={offset}
              onChange={(e) => setOffset(e.target.value)}
            />
            <span className='shrink-0'>h</span>
          </div>
        </Field>
        <Field
          label='Estimated time'
          optional
          htmlFor='ops-task-baseline'
          hint='Also used as the work-speed baseline.'
        >
          <div className='flex items-center gap-2 text-[14px] text-text-secondary'>
            <TextInput
              id='ops-task-baseline'
              type='number'
              inputMode='decimal'
              min={0.5}
              step={0.5}
              value={baseline}
              onChange={(e) => setBaseline(e.target.value)}
            />
            <span className='shrink-0'>h</span>
          </div>
        </Field>
      </div>
      <div className='flex flex-col gap-2'>
        <span className='text-[13px] font-medium leading-[140%] text-text-primary'>
          Comes after <span className='font-normal text-text-secondary'>(optional)</span>
        </span>
        {before.length === 0 ? (
          <span className='text-[13px] text-text-secondary'>No earlier tasks.</span>
        ) : (
          <div className='flex flex-wrap gap-2'>
            {before.map((t) => {
              const on = dependsOn.includes(t.code);
              return (
                <button
                  key={t.id}
                  type='button'
                  aria-pressed={on}
                  title={t.title}
                  onClick={() =>
                    setDependsOn((xs) => (on ? xs.filter((x) => x !== t.code) : [...xs, t.code]))
                  }
                  className={cn(
                    'h-8 rounded-full border px-3 text-[13px] transition-colors',
                    on
                      ? 'border-green-deep02 bg-surface-green10 font-medium text-green-deep04'
                      : 'border-green-deep03/20 text-text-primary hover:bg-grayscale-gray01',
                  )}
                >
                  {t.code}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {receiverCandidates.length > 1 ? (
        // 받는 직무에 사람이 여럿이면 고른다 — 비우면 시스템이 정한다 (규칙 v1.2)
        <Field
          label='Receiver'
          optional
          htmlFor='ops-task-receiver'
          hint='Several people have this role. Leave it on Auto to let the system pick one.'
        >
          <Select
            id='ops-task-receiver'
            value={pickedReceiver ? String(pickedReceiver.id) : ''}
            onChange={(e) => setReceiverPick(e.target.value)}
          >
            <option value=''>
              Auto — {autoReceiver.id == null ? 'system picks' : memberLabel(autoReceiver)}
            </option>
            {receiverCandidates.map((m) => (
              <option key={m.id} value={m.id}>
                {memberLabel(m)}
              </option>
            ))}
          </Select>
        </Field>
      ) : (
        <div className='flex items-center gap-3 rounded-lg bg-grayscale-gray01 px-3.5 py-3 text-[14px]'>
          <span className='text-text-secondary'>Receiver</span>
          <span className='font-medium text-text-primary'>
            {!role
              ? '—'
              : receiver.id == null
                ? `Staff · ${jobLabel(receiver.job)}`
                : memberLabel(receiver)}
          </span>
          <span className='ml-auto'>
            <StatusChip chip={{ label: pickedReceiver ? 'Picked' : 'Auto', tone: 'gray' }} />
          </span>
        </div>
      )}
      <Field
        label='Task List'
        required
        htmlFor='ops-task-list'
        hint='Required items for the handoff type, plus any brief items.'
      >
        <div className='flex flex-col gap-3'>
          {!role && (
            <span className='text-[13px] text-text-secondary'>
              Choose a role to see the base items for its handoff type.
            </span>
          )}
          {base.length > 0 && <TaskListGroups type={baseType} base={base} items={[]} />}
          <div className='flex flex-col gap-1.5'>
            <span className='text-[13px] leading-[140%] text-text-secondary'>
              Brief items{base.length > 0 ? ' (optional)' : ''}
            </span>
            <TaskListEditor
              id='ops-task-list'
              items={items}
              onChange={setItems}
              startAt={base.length}
              placeholder={
                base.length ? 'Add a brief item (optional)' : 'Add an item the receiver will check'
              }
              ariaLabel='Brief items'
            />
          </div>
        </div>
      </Field>
      {tagOptions.length > 0 && (
        <div className='flex flex-col gap-2'>
          <span className='text-[13px] font-medium leading-[140%] text-text-primary'>
            Skill tags <span className='font-normal text-text-secondary'>(optional)</span>
          </span>
          <div className='flex flex-wrap gap-2'>
            {tagOptions.map((t) => {
              const on = tags.includes(t);
              return (
                <button
                  key={t}
                  type='button'
                  aria-pressed={on}
                  onClick={() => setTags((xs) => (on ? xs.filter((x) => x !== t) : [...xs, t]))}
                  className={cn(
                    'h-8 rounded-full border px-3 text-[13px] transition-colors',
                    on
                      ? 'border-green-deep02 bg-surface-green10 font-medium text-green-deep04'
                      : 'border-green-deep03/20 text-text-primary hover:bg-grayscale-gray01',
                  )}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </Modal>
  );
};

/**
 * @param newTeamsOnly 시작 뒤 — 워크보드가 아직 열리지 않은 팀(나중에 더한 팀 등)의 계획만. 워크보드를 처음 열 때 이 계획으로 태스크가 만들어진다
 */
export const TasksStep = ({ project, dispatch, api, onSaved, newTeamsOnly = false }) => {
  const teams = newTeamsOnly ? project.teams.filter((t) => !t.boardStarted) : project.teams;
  const initial = project.tasks;
  const [tasks, setTasks] = useDraft(initial);
  const [pickedTeamId, setTeamId] = useState(null);
  // 팀이 나중에 저장돼도 첫 팀을 기본으로 보여 준다
  const teamId = teams.some((t) => t.id === pickedTeamId) ? pickedTeamId : (teams[0]?.id ?? null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const dirty = !same(tasks, initial);
  useReportDirty(dispatch, 'tasks', dirty);
  const team = teams.find((t) => t.id === teamId);
  const list = tasks[teamId] ?? [];
  const mode = setupMode(project);
  const locked = !newTeamsOnly && mode !== 'draft';

  // 팀원이 나중에 들어오면 비어 있던 담당자, 팀장으로 돌려 둔 받는 사람을 직무에 맞는 팀원으로 채운다
  const memberOfRole = (job) => team?.members.find((m) => !m.staff && m.job === job);
  const fill = (t) => ({
    ...t,
    assigneeId: t.assigneeId ?? memberOfRole(t.role)?.id ?? null,
    // 받을 직무의 신청자 팀원이 생기면 스태프·미정 대신 그 팀원이 받는다 (직접 고른 받는 사람은 그대로)
    receiverId:
      !t.receiverPinned && t.defaultReceiverRole && memberOfRole(t.defaultReceiverRole)
        ? memberOfRole(t.defaultReceiverRole).id
        : t.receiverId,
  });
  if (!locked && team && list.some((t) => !same(fill(t), t))) {
    setTasks((all) => ({ ...all, [teamId]: (all[teamId] ?? []).map(fill) }));
  }

  const everyone = useMemo(() => [...(team?.members ?? []), NPC_LEAD], [team]);
  const personOf = (id) => everyone.find((m) => m.id === id);
  // 등록된 스태프 — 팀에 그 직무 팀원이 없을 때 대신 맡거나 받는다 (미리 보기용)
  const [staffList, setStaffList] = useState([]);
  useEffect(() => {
    getOpsStaff()
      .then((x) => setStaffList(x ?? []))
      .catch(() => setStaffList([]));
  }, []);

  const [saving, runSave] = useSaving();
  const save = () => runSave(() => api.saveTasks(tasks), onSaved);

  return (
    <StepCard
      title={newTeamsOnly ? 'Tasks for teams added later' : 'Tasks'}
      desc={
        newTeamsOnly
          ? 'These teams’ workboards haven’t opened yet. Their tasks are created from this plan when a member first opens the workboard.'
          : 'Tasks you set before the project, in handoff order. Receivers are set by job. Every task needs at least one Task List item.'
      }
      notice={newTeamsOnly ? null : lockedNotice(mode)}
      warning={
        newTeamsOnly
          ? 'Due times follow the leader start, so some may already be past. Set them before members open the workboard. Teams already working aren’t changed here.'
          : null
      }
      hint={!dirty ? 'No changes to save' : null}
      action={locked ? null : 'Save'}
      disabled={saving || !dirty}
      onAction={save}
    >
      {teams.length === 0 ? (
        <EmptyBox title='No teams yet' desc='Create teams in Team & roles first.' />
      ) : (
        <>
          <div className='flex flex-wrap items-center justify-between gap-3'>
            <div
              role='tablist'
              aria-label='Team'
              className='flex w-fit flex-wrap gap-0.5 rounded-xl bg-grayscale-gray02/70 p-1'
            >
              {teams.map((t) => (
                <button
                  key={t.id}
                  type='button'
                  role='tab'
                  aria-selected={t.id === teamId}
                  onClick={() => setTeamId(t.id)}
                  className={cn(
                    'h-8 rounded-lg px-3.5 text-[14px] transition-colors',
                    t.id === teamId
                      ? 'bg-grayscale-gray00 font-medium text-text-primary shadow-[0_1px_3px_rgba(0,0,0,0.12)]'
                      : 'text-text-secondary hover:text-text-primary',
                  )}
                >
                  {t.name}
                </button>
              ))}
            </div>
            {!locked && (
              <TdButton
                variant='soft'
                disabled={!team?.members.length}
                title={team?.members.length ? undefined : 'Add members to this team first'}
                onClick={() => setAdding(true)}
              >
                + Add task
              </TdButton>
            )}
          </div>

          {list.length === 0 ? (
            <EmptyBox
              title={`No tasks for ${team?.name} yet`}
              desc={
                team?.members.length
                  ? 'Optional. The PM plans the team’s tasks in the hour before the team starts. Add tasks here only if you need them.'
                  : 'Add members in Team & roles first.'
              }
            />
          ) : (
            // 기획서 표와 같은 순서: # · Role · Task · Receiving role · Suggested deadline · Comes after
            <div className='overflow-x-auto rounded-xl border border-green-deep03/[0.08] bg-grayscale-gray00 shadow-4'>
              <table className='w-full min-w-[880px] text-left text-[14px] text-text-primary'>
                <thead className='bg-grayscale-gray01 text-[12px] font-medium text-text-secondary'>
                  <tr>
                    <th className='w-[52px] px-4 py-2.5 font-medium'>#</th>
                    <th className='w-[150px] px-4 py-2.5 font-medium'>Role</th>
                    <th className='px-4 py-2.5 font-medium'>Task</th>
                    <th className='w-[150px] px-4 py-2.5 font-medium'>Receiving role</th>
                    <th className='w-[140px] px-4 py-2.5 font-medium'>Suggested deadline</th>
                    <th className='w-[100px] px-4 py-2.5 font-medium'>Comes after</th>
                    {!locked && <th className='w-[110px] px-4 py-2.5' />}
                  </tr>
                </thead>
                <tbody>
                  {list.map((t, i) => (
                    <tr key={t.id} className='border-t border-green-deep03/[0.08] align-top'>
                      <td className='px-4 py-3 font-medium text-text-secondary'>
                        {t.code ?? i + 1}
                      </td>
                      <td className='px-4 py-3'>
                        <span className='block'>{jobLabel(t.role)}</span>
                        <span className='text-[12px] text-text-secondary'>
                          {t.assigneeId != null && personOf(t.assigneeId) ? (
                            <PersonName person={personOf(t.assigneeId)} />
                          ) : personForRole(team.members, t.role, staffList) ? (
                            // 저장하면 그 직무의 팀원 → 스태프가 맡는다
                            <PersonName
                              person={personForRole(team.members, t.role, staffList)}
                              auto
                            />
                          ) : (
                            // 팀원도 스태프도 없을 때만 — 막는 경고가 아니라 안내
                            <span title={`Assigned automatically when a ${jobLabel(t.role)} joins`}>
                              Unassigned · auto
                            </span>
                          )}
                        </span>
                      </td>
                      <td className='px-4 py-3'>
                        <span className='block font-medium'>{t.title}</span>
                        <span className='text-[12px] text-text-secondary'>
                          {templateItemCount(t)} Task List item{templateItemCount(t) > 1 ? 's' : ''}
                          {t.baselineMinutes ? ` · Est. ${formatMinutes(t.baselineMinutes)}` : ''}
                        </span>
                      </td>
                      <td className='px-4 py-3'>
                        <span className='block'>{receivingRoleLabel(t)}</span>
                        {t.receiverId !== NPC_LEAD.id && (
                          <span className='text-[12px] text-text-secondary'>
                            {t.receiverId != null && personOf(t.receiverId) ? (
                              <PersonName person={personOf(t.receiverId)} />
                            ) : personForRole(team.members, t.defaultReceiverRole, staffList) ? (
                              <PersonName
                                person={personForRole(
                                  team.members,
                                  t.defaultReceiverRole,
                                  staffList,
                                )}
                                auto
                              />
                            ) : (
                              'Team lead Kim'
                            )}
                          </span>
                        )}
                      </td>
                      <td className='px-4 py-3'>
                        <span className='block'>T0+{t.dueOffsetHours}h</span>
                        <span className='text-[12px] text-text-secondary'>
                          {formatKst(t.dueAt, { year: false })} KST
                        </span>
                      </td>
                      <td className='px-4 py-3'>
                        {t.dependsOn?.length ? t.dependsOn.join(', ') : '–'}
                      </td>
                      {!locked && (
                        <td className='px-4 py-3'>
                          <span className='flex justify-end gap-3'>
                            <button
                              type='button'
                              onClick={() => setEditing(t)}
                              className='text-[12px] text-text-secondary hover:text-text-primary'
                            >
                              Edit
                            </button>
                            <button
                              type='button'
                              onClick={() =>
                                setTasks((all) => ({
                                  ...all,
                                  [teamId]: list.filter((x) => x.id !== t.id),
                                }))
                              }
                              className='text-[12px] text-text-secondary hover:text-functional-failed04'
                            >
                              Remove
                            </button>
                          </span>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {editing && team && (
        <AddTaskModal
          team={team}
          project={project}
          list={list}
          staffList={staffList}
          task={editing}
          onClose={() => setEditing(null)}
          onAdd={(task) => {
            setTasks((all) => ({
              ...all,
              [teamId]: (all[teamId] ?? []).map((x) => (x.id === task.id ? task : x)),
            }));
            setEditing(null);
          }}
        />
      )}
      {adding && team && (
        <AddTaskModal
          team={team}
          project={project}
          list={list}
          staffList={staffList}
          onClose={() => setAdding(false)}
          onAdd={(task) => {
            setTasks((all) => ({ ...all, [teamId]: [...(all[teamId] ?? []), task] }));
            setAdding(false);
          }}
        />
      )}
    </StepCard>
  );
};

/* ── 5. Checkpoints ─────────────────────────────────────── */

export const CheckpointsStep = ({ project, dispatch, api, onSaved }) => {
  const initial = project.checkpoints;
  const [rows, setRows] = useDraft(initial);
  const dirty = !same(rows, initial);
  useReportDirty(dispatch, 'checkpoints', dirty);
  const { endAt } = periodOf(project.leaderStartAt, project.endAt);
  const [saving, runSave] = useSaving();
  const save = () => runSave(() => api.saveCheckpoints(rows), onSaved);
  const mode = setupMode(project);
  const now = useNow();
  // 저장한 뒤에도 · 시작한 뒤에도 고친다(경고). 이미 지난 체크포인트와 체크인이 있는 체크포인트만 막는다. 끝나면 읽기 전용
  const readOnly = false;
  const started = mode !== 'draft';
  const lockReason = (cp) => {
    const original = initial.find((x) => x.id === cp.id) ?? cp;
    if (cp.hasCheckins) return 'Has check-ins';
    if (started && new Date(original.scheduledAt) <= now) return 'Passed';
    return null;
  };
  const editable = (cp) => !readOnly && !lockReason(cp);
  // 시작 뒤에는 지난 시각으로 옮길 수 없다
  const pastPick =
    started &&
    rows.some((r) => {
      const original = initial.find((x) => x.id === r.id);
      return original && original.scheduledAt !== r.scheduledAt && new Date(r.scheduledAt) <= now;
    });
  const saved = !!project.done.checkpoints;

  return (
    <StepCard
      title='Checkpoints'
      desc="Requested checkpoints: 7 check-ins, every 6 hours from team start. Only ops can change their times. They can't be deleted."
      notice={readOnly ? lockedNotice(mode) : null}
      warning={
        started
          ? `${liveLead(mode)} Only upcoming checkpoints can change, and only to a later time. Saving notifies everyone it applies to. Ones with check-ins can’t change.`
          : null
      }
      hint={
        pastPick
          ? 'Pick a time that hasn’t passed'
          : saved && rows.length && !dirty
            ? 'No changes to save'
            : null
      }
      action={readOnly ? null : 'Save'}
      disabled={saving || rows.length === 0 || (saved && !dirty) || pastPick}
      onAction={save}
    >
      {rows.length === 0 ? (
        <EmptyBox
          title='No checkpoints yet'
          desc='Save the brief to create the requested checkpoints.'
        />
      ) : (
        <div className='overflow-x-auto rounded-xl border border-green-deep03/[0.08] bg-grayscale-gray00 shadow-4 lg:max-w-[720px]'>
          <table className='w-full min-w-[560px] text-left text-[14px]'>
            <thead className='bg-grayscale-gray01 text-[12px] font-medium text-text-secondary'>
              <tr>
                <th className='px-4 py-2.5 font-medium'>Name</th>
                <th className='px-4 py-2.5 font-medium'>Type</th>
                <th className='px-4 py-2.5 font-medium'>Time (KST)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((cp) => (
                <tr key={cp.id} className='border-t border-green-deep03/[0.08]'>
                  <td className='px-4 py-2.5'>
                    <span className='block font-medium text-text-primary'>{cp.name}</span>
                    <span className='text-[12px] text-text-secondary'>
                      Team start + {cp.offsetH}h
                    </span>
                  </td>
                  <td className='px-4 py-2.5'>
                    <StatusChip chip={{ label: 'Requested', tone: 'gray' }} />
                  </td>
                  <td className='px-4 py-2.5'>
                    {!editable(cp) ? (
                      <div className='flex h-10 max-w-[240px] items-center justify-between gap-2 rounded-lg bg-green-deep03/[0.04] px-3 text-text-primary'>
                        {formatKst(cp.scheduledAt, { year: false })}
                        {lockReason(cp) && (
                          <span className='text-[12px] text-text-secondary'>{lockReason(cp)}</span>
                        )}
                      </div>
                    ) : (
                      <TextInput
                        type='datetime-local'
                        aria-label={`${cp.name} time`}
                        value={toInput(cp.scheduledAt)}
                        max={toInput(endAt)}
                        onChange={(e) => {
                          const iso = fromInput(e.target.value);
                          if (iso)
                            setRows((rs) =>
                              rs.map((r) => (r.id === cp.id ? { ...r, scheduledAt: iso } : r)),
                            );
                        }}
                        className='max-w-[240px]'
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </StepCard>
  );
};

/* ── 6. Start / End ─────────────────────────────────────── */

export const StartEndStep = ({ project, api }) => {
  const [confirmEnd, setConfirmEnd] = useState(false);
  const { endAt } = periodOf(project.leaderStartAt, project.endAt);
  const [saving, runSave] = useSaving();
  const set = (status) => runSave(() => (status === 'active' ? api.start() : api.end()));
  // 팀 파일이 없어도 시작할 수 있다 (경고만)
  const files = useTeamFiles(project.id);
  const noTeamFiles = !!files && files.every((f) => f.pinned);

  return (
    <StepCard title='Start / End' desc='Participants can act only while the project is active.'>
      {project.status === 'draft' && noTeamFiles && (
        <FilesAlert tone='warning' title='No team files added'>
          You can still start the project.
        </FilesAlert>
      )}
      <dl className='grid grid-cols-[120px_1fr] gap-y-3 text-[14px]'>
        <dt className='text-text-secondary'>Status</dt>
        <dd className='font-medium capitalize text-text-primary'>{project.status}</dd>
        <dt className='text-text-secondary'>Start</dt>
        <dd className='text-text-primary'>{formatKst(project.leaderStartAt)} KST</dd>
        <dt className='text-text-secondary'>End</dt>
        <dd className='text-text-primary'>{formatKst(endAt)} KST</dd>
      </dl>
      <div className='flex flex-wrap items-center gap-4 border-t border-green-deep03/[0.08] pt-6'>
        {project.status === 'draft' && (
          <>
            <ModalButton primary disabled={saving} onClick={() => set('active')}>
              Start project
            </ModalButton>
            <span className='text-[13px] text-text-secondary'>
              Draft → active. Participants can act only while active.
            </span>
          </>
        )}
        {project.status === 'active' && (
          <>
            <TdButton variant='danger' onClick={() => setConfirmEnd(true)}>
              End project
            </TdButton>
            <span className='text-[13px] text-text-secondary'>
              Active → ended. Workboards become read-only.
            </span>
          </>
        )}
        {project.status === 'ended' && (
          <span className='text-[13px] text-text-secondary'>
            This project has ended. Workboards are read-only.
          </span>
        )}
      </div>

      {confirmEnd && (
        <Modal
          title='End this project?'
          subtitle={project.name}
          onClose={() => setConfirmEnd(false)}
          footer={
            <>
              <ModalButton onClick={() => setConfirmEnd(false)}>Cancel</ModalButton>
              <ModalButton
                primary
                disabled={saving}
                onClick={() => {
                  set('ended');
                  setConfirmEnd(false);
                }}
              >
                End project
              </ModalButton>
            </>
          }
        >
          <p className='text-[14px] leading-[150%] text-text-secondary'>
            Members can't act on the workboard after this. You can't undo it.
          </p>
        </Modal>
      )}
    </StepCard>
  );
};
